import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import { APIKeysPlatformAPI } from "../api-keys-api"
import { UserPlatformAPI } from "../user-api"
import { showNotification } from "aldoo-ra/Notification"
import {  
  PencilIcon, 
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from "@heroicons/react/24/outline"
import APIKeyForm from "./APIKeyForm"

const APIKeysListTab = forwardRef(({ 
  refreshKey, 
  onRefresh, 
  onError, 
  loading, 
  setLoading,
  availablePermissions
}, ref) => {
  const [apiKeys, setAPIKeys] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(25)
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    userId: ''
  })
  const [userFilter, setUserFilter] = useState(null)

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false)
  const [selectedKey, setSelectedKey] = useState(null)

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openCreateModal: () => setShowCreateModal(true)
  }), [])

  // Load API keys
  const loadAPIKeys = useCallback(async () => {
    try {
      setLoading(true)
      
      // Get selected user ID from userFilter
      const selectedUserId = userFilter && userFilter.length > 0 ? userFilter[0]._id : null
      
      const response = await APIKeysPlatformAPI.ListAPIKeys({
        status: filters.status || undefined,
        page: currentPage,
        limit: itemsPerPage
      })


      if (response.result) {
        // Apply client-side search filter if needed
        let filteredKeys = response.keys
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase()
          filteredKeys = response.keys.filter(key => 
            key.description?.toLowerCase().includes(searchTerm) ||
            key.key?.toLowerCase().includes(searchTerm) ||
            key.issuedBy?.toLowerCase().includes(searchTerm) ||
            key.user?.name?.toLowerCase().includes(searchTerm) ||
            key.user?.email?.toLowerCase().includes(searchTerm)
          )
        }
        
        setAPIKeys(filteredKeys)
        setTotalItems(response.total)
      } else {
        onError(response.error || "Failed to load API keys")
      }
    } catch (error) {
      onError(error.message || "Failed to load API keys")
    } finally {
      setLoading(false)
    }
  }, [currentPage, filters, itemsPerPage, userFilter, onError, setLoading])

  // Load data when component mounts or dependencies change
  useEffect(() => {
    loadAPIKeys()
  }, [loadAPIKeys, refreshKey])

  // Format key preview for display
  const formatKeyPreview = (key) => {
    if (!key || key.length < 12) return key
    return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`
  }

  // Get status badge styling
  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium"
    switch (status) {
      case 'active':
        return `${baseClasses} bg-admin_success text-white`
      case 'expired':
        return `${baseClasses} bg-admin_warning text-white`
      case 'revoked':
        return `${baseClasses} bg-admin_error text-white`
      case 'disabled':
        return `${baseClasses} bg-admin_neutral_3 text-white`
      default:
        return `${baseClasses} bg-admin_neutral_3 text-white`
    }
  }

  // Handle create API key
  const handleCreateSuccess = useCallback((newKey) => {
    showNotification({
      message: `API key "${newKey.description}" created successfully`,
      className: "bg-admin_success text-white",
      duration: 3000
    })
    setShowCreateModal(false)
    onRefresh()
  }, [onRefresh])

  // Handle edit API key
  const handleEditSuccess = useCallback((updatedKey) => {
    showNotification({
      message: `API key "${updatedKey.description}" updated successfully`,
      className: "bg-admin_success text-white",
      duration: 3000
    })
    setShowEditModal(false)
    setSelectedKey(null)
    onRefresh()
  }, [onRefresh])

  // Handle revoke API key
  const handleRevoke = useCallback(async () => {
    if (!selectedKey) return

    try {
      setLoading(true)
      const response = await APIKeysPlatformAPI.RevokeAPIKey(selectedKey.key)
      
      if (response.result) {
        showNotification({
          message: `API key "${selectedKey.description}" revoked successfully`,
          className: "bg-admin_success text-white",
          duration: 3000
        })
        setShowRevokeConfirm(false)
        setSelectedKey(null)
        onRefresh()
      } else {
        onError(response.error || "Failed to revoke API key")
      }
    } catch (error) {
      onError(error.message || "Failed to revoke API key")
    } finally {
      setLoading(false)
    }
  }, [selectedKey, onError, onRefresh, setLoading])

  // Handle copy key to clipboard
  const handleCopyKey = useCallback(async (key) => {
    try {
      await navigator.clipboard.writeText(key)
      showNotification({
        message: "API key copied to clipboard",
        className: "bg-admin_success text-white",
        duration: 2000
      })
    } catch (error) {
      showNotification({
        message: "Failed to copy API key",
        className: "bg-admin_error text-white",
        duration: 2000
      })
    }
  }, [])

  // Table renders for DataTable component
  const renders = [
    {
      id: 'keyPreview',
      title: 'Key Preview',
      headerClassName: "w-[25%]",
      render: (_, row) => (
        <div className="flex items-center">
          <code className="font-mono bg-admin_canvas rounded px-2 py-1">
            {formatKeyPreview(row.key)}
          </code>

          <Button
            variant="admin_icon"
            onClick={() => handleCopyKey(row.key)}
            title="Copy full key to clipboard"
          >
            <DocumentDuplicateIcon className="w-4 h-4" />
          </Button>
        </div>
      )
    },
    {
      id: 'description',
      title: 'Description',
      headerClassName: "w-[30%]",
      render: (value) => (
        <div className="max-w-[250px] truncate">
          {value}
        </div>
      )
    },
    {
      id: 'status',
      title: 'Status',
      headerClassName: "w-[10%]",
      render: (value) => (
        <span className={getStatusBadge(value)}>
          {value}
        </span>
      )
    },
    {
      id: 'user',
      title: 'User',
      headerClassName: "w-[20%]",
      render: (_, row) => {
        if (row.user) {
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {row.user.name || 'Unknown User'}
              </span>

              <span className="text-xs text-admin_text_secondary">
                {row.user.email || 'No email'}
              </span>
            </div>
          )
        }
        return <span className="text-admin_text_secondary">System</span>
      }
    },
    {
      id: 'validUntil',
      title: 'Expires',
      headerClassName: "w-[10%] text-center",
      render: (value) => {
        if (!value) return <span className="block text-admin_text_secondary text-center">
          Never
        </span>

        const expDate = new Date(value)
        const isExpired = expDate < new Date()
        return (
          <span className={isExpired ? "text-admin_error" : "text-admin_text_secondary"}>
            {expDate.toLocaleDateString()}
          </span>
        )
      }
    },
    {
      id: 'actions',
      title: 'Actions',
      headerClassName: "w-[10%] text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center">
          <Button
            variant="admin_icon"
            onClick={() => {
              setSelectedKey(row)
              setShowEditModal(true)
            }}
            title="Edit API key"
          >
            <PencilIcon className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ]

  return (
    <div className="space-y-4">
      {/* Actions and Filters */}
      <div className="flex flex-col gap-4">
        {/* Filters left, Search right */}
        <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
          {/* Left side: Filters */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            {/* Status Filter */}
            <div className="w-full sm:w-40">
              <DropdownSelect
                variant="admin"
                value={filters.status ? [{ value: filters.status, name: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) }] : []}
                onChange={(selected) => {
                  setFilters(prev => ({ ...prev, status: selected[0]?.value || '' }))
                  setCurrentPage(1) // Reset to first page when filtering
                }}
                options={[
                  { value: '', name: 'All Status' },
                  { value: 'active', name: 'Active' },
                  { value: 'expired', name: 'Expired' },
                  { value: 'revoked', name: 'Revoked' },
                  { value: 'disabled', name: 'Disabled' }
                ]}
                multiSelection={false}
                placeholder="All Status"
                className="w-full"
              />
            </div>

            {/* User Filter */}
            <div className="w-80">
              <DropdownSelect
                variant="admin"
                searchEnabled
                useExternalSearch
                enableResetButton
                showCheckbox={false}
                apiMethod={async (params) => {
                  if (!params.searchTerm || params.searchTerm.length < 2) {
                    return { data: [] }
                  }

                  const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
                  return response
                }}
                onDataLoaded={(rawData) => {
                  return rawData.map(user => ({
                    _id: user._id,
                    name: user.name || 'Unknown User',
                    email: user.email || 'No email',
                    value: user._id,
                    label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
                    ...user
                  }))
                }}
                searchField="name email"
                valueField="_id"
                nameField="label"
                placeholder="Filter by user..."
                noDataMessage="Type to search users"
                value={userFilter}
                onChange={(value) => {
                  setUserFilter(value)
                  setCurrentPage(1) // Reset to first page when filtering
                }}
                onValueItemRender={(user) => {
                  if (!user) return "Filter by user..."
                  return (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium text-xs">
                          {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {user.name || 'Unknown User'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {user.email || 'No email'}
                        </div>
                      </div>
                    </div>
                  )
                }}
                onItemRender={(user) => (
                  <div className="flex items-center gap-3 p-2">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium text-sm">
                        {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {user.name || 'Unknown User'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {user.email || 'No email'}
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          {/* Right side: Search */}
          <div className="w-full lg:w-80">
            <Input
              variant="admin"
              icon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />}
              type="text"
              placeholder="Search keys..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* API Keys Table */}
      <DataTable
        variant="admin_browser"
        data={apiKeys}
        localUserPreferencesKey="admin.platformAccess.apiKeys"
        renders={renders}
        loading={loading}
        preferredHeight="60vh"
        itemHeight="h-[60px]"
        showSearch={false}
        pagination={{
          currentPage,
          totalItems,
          itemsPerPage,
          onPageChange: setCurrentPage
        }}
        styleOverrides={{
          table: "w-full table-fixed"
        }}
        noData={
          <div className="text-center py-8">
            <Typography variant="admin_body">
              No API keys found
            </Typography>

            <Typography 
              variant="admin_body_dim" 
              className="mt-1 mb-0"
            >
              {userFilter && userFilter.length > 0 
                ? `No API keys found for selected user`
                : filters.status 
                  ? `No ${filters.status} API keys found`
                  : 'Create your first API key to get started'
              }
            </Typography>
          </div>
        }
      />

      {/* Create API Key Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New API Key"
        variant="admin_centered"
      >
        <APIKeyForm
          mode="create"
          onSuccess={handleCreateSuccess}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* Edit API Key Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedKey(null)
        }}
        title="Edit API Key"
        variant="admin_centered"
      >
        {selectedKey && (
          <APIKeyForm
            mode="edit"
            apiKey={selectedKey}
            onSuccess={handleEditSuccess}
            onCancel={() => {
              setShowEditModal(false)
              setSelectedKey(null)
            }}
            onDelete={(key) => {
              setShowEditModal(false)
              setSelectedKey(null)
              onRefresh()
            }}
          />
        )}
      </Modal>


      {/* Revoke Confirmation Modal */}
      <Modal
        isOpen={showRevokeConfirm}
        onClose={() => {
          setShowRevokeConfirm(false)
          setSelectedKey(null)
        }}
        title="Confirm Revoke API Key"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon 
              className="w-6 h-6 text-admin_warning flex-shrink-0 mt-0.5"
            />

            <div>
              <Typography variant="admin_body">
                Are you sure you want to revoke the API key "{selectedKey?.description}"?
              </Typography>

              <Typography variant="admin_txt1_dim">
                This action cannot be undone. The API key will immediately stop working.
              </Typography>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="admin_secondary"
              onClick={() => {
                setShowRevokeConfirm(false)
                setSelectedKey(null)
              }}
            >
              Cancel
            </Button>

            <Button
              variant="admin_error"
              onClick={handleRevoke}
              loading={loading}
            >
              Revoke API Key
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
})

export default APIKeysListTab